import { BasketValidationStatus } from '@epicuro-next/data/baskets';
import {
  BasketItemErrorCode,
  BasketItemErrorType,
} from '@epicuro-next/models/basket';
import { BasketButtonState } from '@epicuro-next/state/basket-state';
import {
  getDatesDiff,
  isAfter,
  isNonNullish,
  isNullish,
  unique,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import {
  BasketStatus,
  ReviewStatus,
  Severity,
  ValidationResult,
} from 'graphql-api/baskets-api-interfaces';

import { selectRemoveErrorItemsEnabledRule } from './basket-configuration-rules.selectors';
import {
  selectBasketData,
  selectBasketReviewStatus,
  selectBasketRowLines,
  selectBasketTotals,
  selectExtendedBasketItemsMap,
  selectIsBasketReviewNeeded,
  selectOpenedDtoBasketFromEntity,
} from './basket-data.selectors';
import { selectBasketDataState } from './feature.selectors';

export const selectValidationResults = createSelector(
  selectBasketDataState,
  (state) => state.validations,
);

export const selectIsValidationSubmitted = createSelector(
  selectBasketDataState,
  (basketData) => basketData.isValidationSubmitted,
);

export const selectCheckOrderExpiryDate = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basketData) => basketData?.checkOrderExpiryUtc ?? null,
);

export const selectIsBasketChecked = createSelector(
  selectCheckOrderExpiryDate,
  (checkOrderExpiryDate) =>
    !!checkOrderExpiryDate &&
    isAfter(new Date(checkOrderExpiryDate), new Date()),
);

export const selectCheckOrderExpiryDuration = createSelector(
  selectCheckOrderExpiryDate,
  (checkOrderExpiryDate: string) =>
    getDatesDiff(new Date(), new Date(checkOrderExpiryDate)),
);

export const selectBasketItemValidations = createSelector(
  selectValidationResults,
  (validations) => validations?.items,
);

export const selectErroredBasketItemIds = createSelector(
  selectBasketItemValidations,
  (itemValidationResult) =>
    (itemValidationResult ?? [])
      .filter((itemValidation) =>
        itemValidation.validationResults?.some(hasSeverityBlocker),
      )
      .map((validation) => validation.basketItemId),
);

export const selectInvalidBasketItemsToRemove = createSelector(
  selectBasketItemValidations,
  selectExtendedBasketItemsMap,
  selectRemoveErrorItemsEnabledRule,
  (items, map, removeErrorItems) =>
    (items ?? [])
      .filter((item) =>
        item.validationResults?.some(
          (validation) =>
            hasJ2Error(validation) ||
            hasJ0Error(validation) ||
            (hasError(validation) && removeErrorItems),
        ),
      )
      .map((item) => map.get(item.basketItemId))
      .filter(isNonNullish),
);

export const selectBasketValidationFunctionResults = createSelector(
  selectBasketDataState,
  (state) => state.functionValidations ?? [],
);

export const selectHasFunctionValidationError = createSelector(
  selectBasketValidationFunctionResults,
  (validationResults) => !!validationResults.length,
);
/**
 * Selects the basket validation results
 */
export const selectBasketValidationErrors = createSelector(
  selectValidationResults,
  selectBasketValidationFunctionResults,
  (validationResults, functionValidations) =>
    functionValidations
      .concat(validationResults?.validationResults ?? [])
      .filter((result) => hasSeverityNonBlocker(result))
      .filter(
        (result, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              ((t.message && result.message && t.message === result.message) ||
                t.translationKey === result.translationKey) &&
              t.severity === result.severity,
          ),
      ),
);

export const selectHasBasketLevelValidations = createSelector(
  selectBasketValidationErrors,
  (errors) => Boolean(errors?.length),
);

export const selectBasketItemsValidationResults = createSelector(
  selectBasketItemValidations,
  (itemValidations) =>
    itemValidations?.flatMap((item) => item.validationResults),
);

export const selectBackendIsNotAvailable = createSelector(
  selectBasketRowLines,
  (rows) => rows.some((row) => isNullish(row.data.total?.confirmed?.quantity)),
);

export const selectCustomQuantityErrors = createSelector(
  selectIsBasketChecked,
  selectBasketRowLines,
  selectBasketTotals,
  selectBackendIsNotAvailable,
  (isBasketChecked, rows, totals, backendNotAvailable) => {
    const errors: BasketItemErrorType[] = [];
    if (!isBasketChecked) {
      return null;
    }
    if (rows?.some((row) => row.data.total?.confirmed?.quantity === 0)) {
      errors.push(BasketItemErrorType.BASKET_ROW_NULL_ERROR);
    }

    if (
      totals?.confirmed?.quantity !== totals?.requested?.quantity &&
      !backendNotAvailable
    ) {
      errors.push(BasketItemErrorType.BASKET_TOTAL_DIFFERENT_QUANTITIES);
    }

    if (totals?.confirmed?.quantity === 0) {
      errors.push(BasketItemErrorType.BASKET_TOTAL_ZERO);
    }

    return errors;
  },
);

export const selectBasketItemLevelValidationsErrors = createSelector(
  selectBasketItemsValidationResults,
  (itemsValidations) =>
    itemsValidations
      ?.map(getItemErrorType)
      .filter((v): v is BasketItemErrorType => !!v)
      .filter(unique),
);

export const selectBasketItemsErrors = createSelector(
  selectBasketItemLevelValidationsErrors,
  selectCustomQuantityErrors,
  (errors, quantityErrors) => [...(errors ?? []), ...(quantityErrors ?? [])],
);

export const selectBasketItemsBlockingErrors = createSelector(
  selectBasketItemsErrors,
  (errors) => errors.filter(hasBasketItemBlockerError),
);

export const selectBasketItemsWarningErrors = createSelector(
  selectBasketItemsErrors,
  (errors) => errors.filter((error) => !hasBasketItemBlockerError(error)),
);

export const selectPrioritizedBasketItemsWarning = createSelector(
  selectBasketItemsWarningErrors,
  (errors) => {
    const prioritiesOrder = Object.keys(BasketItemErrorType);
    return [...errors].sort(
      (a, b) => prioritiesOrder.indexOf(a) - prioritiesOrder.indexOf(b),
    );
  },
);

// UI shows only one warning message, so we get prioritized one
export const selectHighestBasketItemWarning = createSelector(
  selectPrioritizedBasketItemsWarning,
  (errors) => (errors ?? [])[0],
);

export const selectBasketValidationStatus = createSelector(
  selectValidationResults,
  selectBasketItemsBlockingErrors,
  selectIsBasketChecked,
  (basketValidations, invalidItems, basketIsChecked) => {
    if (basketValidations) {
      const basketIsInValid =
        basketValidations.validationResults?.some(hasSeverityBlocker);

      if (basketIsInValid || invalidItems.length) {
        return BasketValidationStatus.Invalid;
      }
    }

    if (basketIsChecked) {
      return BasketValidationStatus.Valid;
    }

    return BasketValidationStatus.Invalid;
  },
);

export const selectIsBasketStatusValid = createSelector(
  selectBasketValidationStatus,
  (status) => status === BasketValidationStatus.Valid,
);

export const isBasketStatusValidAndTotalNotZero = createSelector(
  selectBasketValidationStatus,
  selectHighestBasketItemWarning,
  (validationStatus, highestBasketItemError) =>
    validationStatus === BasketValidationStatus.Valid &&
    highestBasketItemError === BasketItemErrorType.BASKET_TOTAL_ZERO,
);

export const selectBasketButtonState = createSelector(
  selectIsBasketReviewNeeded,
  selectBasketReviewStatus,
  selectBasketValidationStatus,
  (reviewNeeded, reviewStatus, validationStatus) => {
    if (reviewNeeded && reviewStatus === ReviewStatus.Pending) {
      return BasketButtonState.WaitingForReview;
    }

    if (validationStatus === BasketValidationStatus.Invalid) {
      if (!reviewNeeded && reviewStatus === ReviewStatus.Pending) {
        return BasketButtonState.RejectOrCheck;
      }

      return BasketButtonState.CheckOrder;
    }

    if (!reviewNeeded && reviewStatus === ReviewStatus.Pending) {
      return BasketButtonState.RejectOrPlace;
    }

    if (reviewNeeded && reviewStatus !== ReviewStatus.Pending) {
      return BasketButtonState.SendForReview;
    }

    return BasketButtonState.PlaceOrder;
  },
);

export const selectReviewerMessage = createSelector(
  selectBasketButtonState,
  selectBasketData,
  (basketButtonState, basketData) => {
    if (
      basketData?.reviewStatus === ReviewStatus.Pending &&
      basketData.status !== BasketStatus.New
    ) {
      return {
        message: $localize`:Cbasket|Review accepted alert message@@reviewAcceptedAlertMessage:This basket was reviewed and accepted by <strong>${basketData?.assignee?.displayName}.</strong> You can contact them at the following address: <a href="mailto:${basketData?.assignee?.email}">${basketData?.assignee?.email}</a>`,
        severity: Severity.Success,
      };
    } else if (basketButtonState === BasketButtonState.WaitingForReview) {
      return {
        message: $localize`:Cbasket|Waiting for review alert message@@waitingForReviewAlertMessage:Your order is waiting to be reviewed by <strong>${basketData?.assignee?.displayName}.</strong> You can contact them at the following address: <a href="mailto:${basketData?.assignee?.email}">${basketData?.assignee?.email}</a>`,
        severity: Severity.Information,
      };
    } else if (
      basketButtonState === BasketButtonState.RejectOrCheck ||
      basketButtonState === BasketButtonState.RejectOrPlace
    ) {
      return {
        message: $localize`:Cbasket|You are the reviewer alert message@@youAreTheReviewerAlertMessageText:This basket has been assigned to <strong>${basketData?.assignee?.displayName}</strong> (<a href="mailto:${basketData?.assignee?.email}">${basketData?.assignee?.email}</a>) to review from <strong>${basketData?.assigner?.displayName}</strong> (<a href="mailto:${basketData?.assigner?.email}">${basketData?.assigner?.email}</a>).`,
        severity: Severity.Information,
      };
    } else if (
      basketData?.reviewNeeded &&
      basketData?.reviewStatus === ReviewStatus.Rejected
    ) {
      return {
        message: $localize`:Cbasket|Review rejected assigner alert message@@reviewRejectedAssignerAlertMessageText:This basket was reviewed and rejected by <strong>${
          basketData?.assignee?.displayName ?? 'assignee'
        }.</strong> You can contact them at the following address: <a href="mailto:${
          basketData?.assigner?.email
        }">${basketData?.assigner?.email}</a>`,
        severity: Severity.Error,
      };
    } else if (
      !basketData?.reviewNeeded &&
      basketData?.reviewStatus === ReviewStatus.Rejected
    ) {
      return {
        message: $localize`:Cbasket|Review rejected assignee alert message@@reviewRejectedAssigneeAlertMessage:You have requested edits to a basket created by <strong>${basketData?.assignee?.displayName}.</strong> You can contact them at the following address: <a href="mailto:${basketData?.assignee?.email}">${basketData?.assignee?.email}</a>`,
        severity: Severity.Error,
      };
    }
  },
);

export function hasSeverityBlocker(item: ValidationResult): boolean {
  const severityList = [Severity.Critical, Severity.Error];
  return severityList.includes(item.severity);
}

export function hasSeverityNonBlocker(item: ValidationResult): boolean {
  const severityList = [Severity.Critical, Severity.Error, Severity.Warning];
  return severityList.includes(item.severity);
}

export const getItemErrorType = (
  validation: ValidationResult,
): BasketItemErrorType | null => {
  if (hasJ0Error(validation)) return BasketItemErrorType.JO_NOW_ERROR;
  if (hasYTError(validation)) return BasketItemErrorType.YT_ERROR;
  if (hasJ2Error(validation)) return BasketItemErrorType.J2_ERROR;
  if (hasError(validation)) return BasketItemErrorType.ITEM_ERROR;
  if (hasJ0Warning(validation)) return BasketItemErrorType.JO_FUTURE_WARNING;
  if (hasWarning(validation)) return BasketItemErrorType.ITEM_WARNING;
  return null;
};

export const hasJ0Error = (validation: ValidationResult) =>
  validation.message === BasketItemErrorCode.J0 &&
  hasSeverityBlocker(validation);
export const hasJ0Warning = (validation: ValidationResult) =>
  validation.message === BasketItemErrorCode.J0 &&
  validation.severity === Severity.Warning;
export const hasYTError = (validation: ValidationResult) =>
  validation.message === BasketItemErrorCode.YT;
export const hasJ2Error = (validation: ValidationResult) =>
  validation.message === BasketItemErrorCode.J2;
export const hasError = (validation: ValidationResult) =>
  hasSeverityBlocker(validation);
export const hasWarning = (validation: ValidationResult) =>
  validation.severity === Severity.Warning;
export const hasBasketItemBlockerError = (error: BasketItemErrorType) =>
  [
    BasketItemErrorType.JO_NOW_ERROR,
    BasketItemErrorType.J2_ERROR,
    BasketItemErrorType.YT_ERROR,
    BasketItemErrorType.ITEM_ERROR,
  ].includes(error);
